/**
 * AuditLogTable — audit log table with expandable rows and pagination.
 * US-008 Steps 1–4. WCAG 2.1 AA: table scope, live regions.
 */
import React, { useState } from 'react';
import { ChevronDown, ChevronRight, Copy, ScrollText } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { EmptyState } from '@/components/common/EmptyState';
import { AuditLogDetail } from './AuditLogDetail';
import type { AuditLog } from '@/types/index';

const PAGE_SIZE = 50;

type BadgeVariant = 'default' | 'secondary' | 'destructive' | 'outline';

function eventBadgeVariant(t: string): BadgeVariant {
  if (t.includes('failed') || t.includes('deleted')) return 'destructive';
  if (t.includes('completed') || t.includes('submitted') || t.includes('decision')) return 'default';
  if (t.includes('started') || t.includes('created') || t.includes('uploaded')) return 'secondary';
  return 'outline';
}

const shortId = (id: string | null) => (id ? id.slice(0, 8) : '—');

function formatTimestamp(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
}

interface AuditLogTableProps {
  logs: AuditLog[] | undefined; isLoading: boolean; error: Error | null;
  onRetry: () => void; totalCount: number; page: number;
  onPageChange: (page: number) => void; className?: string;
}

export function AuditLogTable({ logs, isLoading, error, onRetry, totalCount, page, onPageChange, className }: AuditLogTableProps) {
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const from = (page - 1) * PAGE_SIZE + 1;
  const to = Math.min(page * PAGE_SIZE, totalCount);

  const toggleRow = (id: string) => { setExpandedId((prev) => (prev === id ? null : id)); };

  const copyJobId = async (id: string) => {
    await navigator.clipboard.writeText(id);
    toast.success('Job ID copied');
  };

  if (isLoading) return (
    <div className="space-y-2" role="status" aria-busy="true" aria-label="Loading audit logs">
      {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-11 w-full" />)}
    </div>
  );

  if (error) return (
    <Alert variant="destructive" role="alert">
      <AlertTitle>Failed to load audit logs</AlertTitle>
      <AlertDescription className="flex items-center gap-3">
        {error.message || 'Please try again.'}
        <Button variant="outline" size="sm" onClick={onRetry}>Retry</Button>
      </AlertDescription>
    </Alert>
  );

  if (!logs || logs.length === 0) return (
    <EmptyState icon={ScrollText}
      title={totalCount === 0 ? 'No audit logs yet' : 'No audit logs match your filters'}
      description={totalCount === 0
        ? 'Audit records will appear here after your first contract review.'
        : 'Adjust the filters above to broaden the results.'} />
  );

  return (
    <div className={className}>
      <p className="mb-2 text-xs text-muted-foreground" aria-live="polite" aria-atomic="true">
        Showing {from}–{to} of {totalCount.toLocaleString()} entries
      </p>

      <div className="overflow-x-auto rounded-md border border-border">
        <table className="w-full text-sm" aria-label="Audit log entries">
          <thead>
            <tr className="border-b border-border bg-muted/30 text-left">
              <th scope="col" className="px-4 py-3 font-medium text-muted-foreground">Timestamp</th>
              <th scope="col" className="px-4 py-3 font-medium text-muted-foreground">Event Type</th>
              <th scope="col" className="px-4 py-3 font-medium text-muted-foreground">Actor</th>
              <th scope="col" className="px-4 py-3 font-medium text-muted-foreground">Job ID</th>
              <th scope="col" className="px-4 py-3 font-medium text-muted-foreground sr-only">Expand</th>
            </tr>
          </thead>
          <tbody>
            {logs.map((log) => {
              const isExpanded = expandedId === log.id;
              const jobId = log.job_id;
              const meta = log.metadata ?? {};
              const stageName = typeof meta['stage'] === 'string' ? meta['stage'] : null;
              const rawNote = meta['reviewer_note'];
              const reviewerNote = typeof rawNote === 'string' && rawNote.trim() ? rawNote.trim() : null;
              return (
                <React.Fragment key={log.id}>
                  <tr
                    className="border-b border-border last:border-0 hover:bg-muted/20 transition-colors"
                  >
                    <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap">
                      {formatTimestamp(log.created_at)}
                    </td>
                    <td className="px-4 py-3 align-top">
                      <div className="flex flex-col gap-1">
                        <div className="flex items-center gap-2">
                          <Badge variant={eventBadgeVariant(log.event_type)} className="text-xs">
                            {log.event_type}
                          </Badge>
                          {stageName && (
                            <span className="text-[11px] font-mono text-muted-foreground">
                              {stageName}
                            </span>
                          )}
                        </div>
                        {reviewerNote && (
                          <p className="max-w-md truncate text-xs italic text-violet-700 dark:text-violet-300"
                             title={reviewerNote}>
                            “{reviewerNote.length > 80 ? `${reviewerNote.slice(0, 80)}…` : reviewerNote}”
                          </p>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-xs font-mono text-muted-foreground">
                      {shortId(log.actor_id ?? null)}
                    </td>
                    <td className="px-4 py-3 text-xs font-mono">
                      {jobId ? (
                        <span className="inline-flex items-center gap-1">
                          {shortId(jobId)}
                          <button type="button" onClick={() => void copyJobId(jobId)}
                            aria-label={`Copy full job ID ${jobId}`}
                            className="text-muted-foreground hover:text-foreground focus-visible:ring-1 focus-visible:ring-ring rounded">
                            <Copy className="h-3 w-3" aria-hidden="true" />
                          </button>
                        </span>
                      ) : '—'}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0"
                        onClick={() => { toggleRow(log.id); }}
                        aria-expanded={isExpanded}
                        aria-label={isExpanded ? 'Collapse details' : 'Expand details'}
                        aria-controls={`audit-detail-${log.id}`}
                      >
                        {isExpanded
                          ? <ChevronDown className="h-4 w-4" aria-hidden="true" />
                          : <ChevronRight className="h-4 w-4" aria-hidden="true" />}
                      </Button>
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr id={`audit-detail-${log.id}`}>
                      <td colSpan={5} className="p-0">
                        <AuditLogDetail log={log} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="mt-3 flex items-center justify-end gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={page === 1}
            onClick={() => { onPageChange(page - 1); }}
            aria-label="Previous page"
          >
            Previous
          </Button>
          <span className="text-xs text-muted-foreground" aria-live="polite">
            Page {page} of {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page === totalPages}
            onClick={() => { onPageChange(page + 1); }}
            aria-label="Next page"
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
}
